
import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, X } from "lucide-react";
import { Message } from "@/types/ticker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface MessageControlProps {
  messages: Array<Message & { is_active: boolean }>;
  allMessages: Message[];
  onAddMessage: (text: string) => void;
  onRemoveMessage: (id: string) => void;
  onUpdateMessage: (id: string, text: string) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
}

export const MessageControl: React.FC<MessageControlProps> = ({
  messages,
  allMessages,
  onAddMessage,
  onRemoveMessage,
  onUpdateMessage,
  onToggleActive,
}) => {
  const [newMessage, setNewMessage] = React.useState("");
  const [selectedMessage, setSelectedMessage] = React.useState<string>("");

  const handleAddMessage = () => {
    if (newMessage.trim()) {
      onAddMessage(newMessage);
      setNewMessage("");
    }
  };

  const handleSelectMessage = (messageId: string) => {
    const message = allMessages.find(m => m.id === messageId);
    if (message && !messages.some(m => m.id === messageId)) {
      onToggleActive(messageId, true);
      setSelectedMessage("");
    }
  };

  // Filter out messages that are already in use
  const availableMessages = allMessages.filter(
    message => !messages.some(m => m.id === message.id)
  );

  return (
    <Card className="p-4 space-y-4 bg-ticker-bg text-ticker-text">
      <div className="flex gap-2">
        <Input
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Enter new message"
          className="flex-1 bg-ticker-muted text-ticker-text border-ticker-accent"
          onKeyPress={(e) => e.key === "Enter" && handleAddMessage()}
        />
        <Button
          onClick={handleAddMessage}
          className="bg-ticker-accent hover:bg-ticker-accent/90"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {availableMessages.length > 0 && (
        <div className="flex gap-2">
          <Select
            value={selectedMessage}
            onValueChange={handleSelectMessage}
          >
            <SelectTrigger className="w-full bg-ticker-muted/10">
              <SelectValue placeholder="Add existing message" />
            </SelectTrigger>
            <SelectContent>
              {availableMessages.map((message) => (
                <SelectItem 
                  key={message.id} 
                  value={message.id}
                  className="cursor-pointer"
                >
                  {message.text}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        {messages.map((message) => (
          <div
            key={message.id}
            className="flex items-center gap-2 p-2 rounded bg-ticker-muted/20 animate-fade-in"
          >
            <Checkbox
              id={`message-${message.id}`}
              checked={message.is_active}
              onCheckedChange={(checked) => onToggleActive(message.id, checked as boolean)}
              className="border-ticker-accent"
            />
            <Input
              value={message.text}
              onChange={(e) => onUpdateMessage(message.id, e.target.value)}
              className="flex-1 bg-transparent border-ticker-muted"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRemoveMessage(message.id)}
              className="text-ticker-text hover:text-red-400 hover:bg-red-400/20"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
};
